import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import type { Theme } from '@mui/material';
import { Alert, Box, Button, InputAdornment, Skeleton, Tooltip, Typography } from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import AddIcon from '@mui/icons-material/Add';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import type { GridColDef, GridRenderCellParams, GridRowProps, RowPropsOverrides } from '@mui/x-data-grid';
import { DataGrid, GridRow } from '@mui/x-data-grid';
import { useQueryClient } from '@tanstack/react-query';
import { isEmpty } from 'lodash-es';
import { parseFlowsString } from 'utils';
import { Select } from 'components/common';
import type { PackEntry } from 'types/shared';
import type { AdSetEntry, AdSettingEntryWithName, AdSettingPageEntry, AdSettingTextEntry } from '../api';
import useAdSettings from '../api/getAdSettings';
import adSettingsMapper from '../utils/adSettings.mapper';
import {
  NO_TEXTS_AVAILABLE_MESSAGE,
  getCreativesByIdFromCache,
  getPackLocalizationIssue,
  toTextId,
} from '../utils/adSettingsLocalization';
import {
  calculatePackFlowAdsCount,
  countAdSetsContainingFlow,
  countPackCreatives,
  getPackDisplayName,
} from '../utils/flowAdSetCount';
import { isScalingEgProcess } from '../utils/marketingProcess';
import { mergeAdSettingsSlot } from '../validation/adSettingsValidation';
import EditLocTextModal from './EditLocTextModal';

const SCALING_EG_CREATIVES_PACK: Omit<PackEntry, 'creatives'> = {
  tag: 'Creatives',
  filterCreativeByName: [],
  visual_touch_ups: true,
};

const FACEBOOK_AD_LIMITS_URL = 'https://business.facebook.com/ads/ad_limits/';

interface AdSettingsPerPackRow {
  id: string;
  packIndex: number;
  flowIndex: number;
  isLastFlowInPack: boolean;
  packLabel: string;
  flowName: string;
  adSetsCount: number;
  adsCount: number;
  pages: AdSettingPageEntry[];
  texts: AdSettingTextEntry[];
}

interface MissingLocalizationIssue {
  packIndex: number;
  flowName: string;
  selectedTextId: number | null;
  message: string;
}

const cloneAdSettingsDefaults = <T,>(value: T): T =>
  JSON.parse(JSON.stringify(value)) as T;

const toRowKey = (packIndex: number, flowName: string): string => `${packIndex}:${flowName}`;

// RHF writes nested values into the existing form-state objects, so `packAdSettings`
// keeps the same identity after a select change. Serialize the ids we depend on so
// memos recompute when a selection actually changes.
const serializeSelectedTextIds = (
  packAdSettings: Record<string, unknown>[] | undefined,
): string =>
  JSON.stringify(
    (packAdSettings ?? []).map((slot) =>
      Object.entries((slot ?? {}) as Record<string, { text_id?: unknown } | undefined>)
        .map(([flowName, setting]) => [flowName, toTextId(setting?.text_id)])
        .sort(([a], [b]) => String(a).localeCompare(String(b))),
    ),
  );

const gridStyles = (t: Theme) => ({
  '& .MuiDataGrid-cell': {
    display: 'flex',
    alignItems: 'center',
    minHeight: '64px !important',
    py: 1,
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    fontWeight: 500,
    fontSize: 14,
  },
  '.MuiDataGrid-row .MuiInputAdornment-positionEnd': {
    visibility: 'hidden',
  },
  '.MuiDataGrid-row:hover .MuiInputAdornment-positionEnd': {
    visibility: 'visible',
  },
  '.MuiDataGrid-cell:focus, .MuiDataGrid-cell:focus-within': {
    outline: 'none',
  },
  '& .pack-group-start': {
    borderTop: 'none',
  },
  '& .pack-group-start:first-of-type': {
    borderTop: 'none',
  },
  '& .pack-group-end .MuiDataGrid-cell': {
    borderBottom: 'none',
  },
  '& .MuiDataGrid-row': {
    minHeight: '64px !important',
    maxHeight: 'none !important',
  },
  backgroundColor: t.palette.background.paper,
});

const CustomGridRow = (props: GridRowProps & RowPropsOverrides) => {
  const row = props.row as AdSettingsPerPackRow;

  return (
    <GridRow
      {...props}
      data-testid={
        row.flowIndex === 0
          ? `adSettingsForm-${row.packIndex}`
          : `adSettingsRow-${row.packIndex}-${row.flowIndex}`
      }
    />
  );
};

const SelectAdSettingsPerPack: FC = () => {
  const { watch, setValue, trigger, getValues } = useFormContext();
  const queryClient = useQueryClient();
  const app = watch('app') as string;
  const platform = watch('platform') as string;
  const marketingProcess = watch('marketingProcess') as string | undefined;
  const adType = watch('adType') as string | undefined;
  const selectedAdSetsRows = watch('selectedAdSets') as AdSetEntry[] | undefined;
  const packsWithCreatives = watch('packsWithCreatives') as PackEntry[] | undefined;
  const selectedCreatives = watch('selectedCreatives') as string[] | undefined;
  const packAdSettings = watch('packAdSettings') as Record<string, unknown>[] | undefined;
  const isScalingEg = isScalingEgProcess(marketingProcess);
  const [editingTextId, setEditingTextId] = useState<number | null>(null);
  const [isEditLocTextModalOpen, setIsEditLocTextModalOpen] = useState(false);

  const adSets = useMemo(
    () =>
      selectedAdSetsRows?.map(({ account_id, name, id, campaign_id, flow }) => ({
        account_id,
        name,
        id,
        campaign_id,
        flow: flow ?? '',
      })) ?? [],
    [selectedAdSetsRows],
  );

  const flows = useMemo(
    () => [...new Set(adSets.flatMap(({ flow }) => parseFlowsString(flow)))],
    [adSets],
  );

  // Scaling EG uses a flat creatives list (no packs). Treat it as one settings slot.
  const packs = useMemo(() => {
    if (isScalingEg) {
      return [
        {
          ...SCALING_EG_CREATIVES_PACK,
          adType,
          creatives: selectedCreatives ?? [],
        },
      ];
    }

    return packsWithCreatives ?? [];
  }, [adType, isScalingEg, packsWithCreatives, selectedCreatives]);
  const packCount = packs.length;
  const canFetchAdSettings = packCount > 0 && flows.length > 0;

  const {
    data: adSettings,
    isError,
    error,
    isSuccess: isSuccessAdSettings,
    isLoading: isLoadingAdSettings,
    refetch: refetchAdSettings,
  } = useAdSettings({
    params: { app, platform, flows },
    isPartnershipAds: false,
    config: {
      select: adSettingsMapper,
      enabled: canFetchAdSettings,
    },
  });

  useEffect(() => {
    const currentSettings = Array.isArray(packAdSettings) ? packAdSettings : [{}];

    if (currentSettings.length === packCount) {
      return;
    }

    const nextSettings = [...currentSettings];
    while (nextSettings.length < packCount) {
      nextSettings.push({});
    }
    while (nextSettings.length > packCount) {
      nextSettings.pop();
    }

    setValue('packAdSettings', nextSettings, { shouldValidate: false });
  }, [packCount, packAdSettings, setValue]);

  useEffect(() => {
    if (!isSuccessAdSettings || !adSettings || packCount < 1) {
      return;
    }

    const defaults = adSettings.reduce<Record<string, { page_id: number | ''; text_id: number | '' }>>(
      (acc, { name, pages, texts }) => {
        const defaultPage = pages?.find((page: AdSettingPageEntry) => page.is_default);
        const defaultText = texts?.find((text: AdSettingTextEntry) => text.is_default);

        return {
          ...acc,
          [name]: {
            page_id: defaultPage?.id ?? '',
            text_id: defaultText?.id ?? '',
          },
        };
      },
      {},
    );

    if (isEmpty(defaults)) {
      return;
    }

    for (let index = 0; index < packCount; index += 1) {
      const existing = (getValues(`packAdSettings.${index}`) as Record<string, unknown> | undefined) ?? {};
      setValue(
        `packAdSettings.${index}`,
        mergeAdSettingsSlot(cloneAdSettingsDefaults(defaults), existing),
        { shouldValidate: false },
      );
    }
    void trigger('packAdSettings');
  }, [adSettings, getValues, isSuccessAdSettings, packCount, setValue, trigger]);

  const rows = useMemo<AdSettingsPerPackRow[]>(() => {
    if (!adSettings?.length || !packs.length) {
      return [];
    }

    return packs.flatMap((pack, packIndex) => {
      const creativesCount = countPackCreatives(pack);
      const packLabel = `${getPackDisplayName(pack, packIndex)} (${creativesCount} creative${creativesCount > 1 ? 's' : ''})`;

      return adSettings.map((setting: AdSettingEntryWithName, flowIndex) => {
        const adSetsCount = countAdSetsContainingFlow(adSets, setting.name);
        const adsCount = calculatePackFlowAdsCount(creativesCount, adSetsCount);

        return {
          id: `${packIndex}-${setting.name}`,
          packIndex,
          flowIndex,
          isLastFlowInPack: flowIndex === adSettings.length - 1,
          packLabel,
          flowName: setting.name,
          adSetsCount,
          adsCount,
          pages: setting.pages ?? [],
          texts: setting.texts ?? [],
        };
      });
    });
  }, [adSets, adSettings, packs]);

  const selectedTextIdsSignature = serializeSelectedTextIds(packAdSettings);

  const selectedTextIdsByPack = useMemo<Map<string, number | null>[]>(
    () =>
      (JSON.parse(selectedTextIdsSignature) as [string, number | null][][]).map(
        (entries) => new Map(entries),
      ),
    [selectedTextIdsSignature],
  );

  const missingLocalizationByRow = useMemo<Record<string, MissingLocalizationIssue>>(() => {
    if (!rows.length) {
      return {};
    }

    const creativesById = getCreativesByIdFromCache(queryClient);

    return rows.reduce<Record<string, MissingLocalizationIssue>>((acc, row) => {
      if (row.adsCount === 0) {
        return acc;
      }

      if (row.texts.length === 0) {
        acc[toRowKey(row.packIndex, row.flowName)] = {
          packIndex: row.packIndex,
          flowName: row.flowName,
          selectedTextId: null,
          message: NO_TEXTS_AVAILABLE_MESSAGE,
        };
        return acc;
      }

      const pack = packs[row.packIndex];
      if (!pack) {
        return acc;
      }

      const selectedTextId = selectedTextIdsByPack[row.packIndex]?.get(row.flowName) ?? null;
      const selectedText = selectedTextId
        ? row.texts.find((text) => text.id === selectedTextId)
        : (row.texts.find((text) => text.is_default) ?? row.texts[0]);
      if (!selectedText) {
        return acc;
      }

      const message = getPackLocalizationIssue(selectedText, pack, creativesById);
      if (!message) {
        return acc;
      }

      acc[toRowKey(row.packIndex, row.flowName)] = {
        packIndex: row.packIndex,
        flowName: row.flowName,
        selectedTextId: selectedText.id,
        message,
      };

      return acc;
    }, {});
  }, [packs, queryClient, rows, selectedTextIdsByPack]);

  useEffect(() => {
    const issuesMap = Object.fromEntries(
      Object.entries(missingLocalizationByRow).map(([key, issue]) => [key, issue.message]),
    );
    setValue('packAdSettingsLocalization', issuesMap, { shouldValidate: false });
    void trigger();
  }, [missingLocalizationByRow, setValue, trigger]);

  const handleAdSettingChange = useCallback(() => {
    void trigger();
  }, [trigger]);

  const handleOpenEditLocTextModal = useCallback((textId: number | null) => {
    setEditingTextId(textId);
    setIsEditLocTextModalOpen(true);
  }, []);

  const handleCloseEditLocTextModal = useCallback(() => {
    setIsEditLocTextModalOpen(false);
  }, []);

  const handleLocTextModalSavedOrClosed = useCallback(() => {
    void refetchAdSettings();
    void trigger();
  }, [refetchAdSettings, trigger]);

  const columns = useMemo<GridColDef<AdSettingsPerPackRow>[]>(
    () => [
      {
        field: 'packLabel',
        headerName: 'Pack',
        width: 220,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params: GridRenderCellParams<AdSettingsPerPackRow>) => {
          if (params.row.flowIndex !== 0) {
            return null;
          }

          return (
            <Typography variant="body2" fontWeight={600}>
              {params.row.packLabel}
            </Typography>
          );
        },
      },
      {
        field: 'flowName',
        headerName: 'Flow',
        width: 200,
        sortable: false,
        disableColumnMenu: true,
      },
      {
        field: 'adSetsCount',
        headerName: 'Ad sets',
        width: 120,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params: GridRenderCellParams<AdSettingsPerPackRow>) =>
          `${params.row.adSetsCount} adset${params.row.adSetsCount === 1 ? '' : 's'}`,
      },
      {
        field: 'adsCount',
        headerName: 'Ads',
        width: 100,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params: GridRenderCellParams<AdSettingsPerPackRow>) =>
          `${params.row.adsCount} Ads`,
      },
      {
        field: 'page',
        headerName: 'Page',
        flex: 1,
        minWidth: 200,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params: GridRenderCellParams<AdSettingsPerPackRow>) => {
          const isDisabled = params.row.adsCount === 0 || !params.row.pages.length;

          return (
              <Box sx={{ width: '100%', py: 0.5 }}>
              <Select
                disabled={isDisabled}
                required={!isDisabled}
                hideErrorMessage
                dataTestId={`adSettings-page-${params.row.packIndex}-${params.row.flowName}`}
                name={`packAdSettings.${params.row.packIndex}.${params.row.flowName}.page_id`}
                sx={{ '.MuiInputBase-root': { backgroundColor: 'white' } }}
                options={params.row.pages.map((page) => ({
                  value: page.id,
                  label: page.name,
                }))}
                handleChange={handleAdSettingChange}
              />
            </Box>
          );
        },
      },
      {
        field: 'text',
        headerName: 'Text',
        flex: 1.4,
        minWidth: 240,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params: GridRenderCellParams<AdSettingsPerPackRow>) => {
          const isDisabled = params.row.adsCount === 0 || !params.row.texts.length;
          const localizationIssue = missingLocalizationByRow[toRowKey(
            params.row.packIndex,
            params.row.flowName,
          )];
          const shouldShowAddLocTextButton =
            params.row.adsCount > 0 &&
            (Boolean(localizationIssue) || params.row.texts.length === 0);

          return (
            <Box sx={{ width: '100%', py: 0.5, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Select
                    disabled={isDisabled}
                    required={!isDisabled}
                    hideErrorMessage
                    dataTestId={`adSettings-text-${params.row.packIndex}-${params.row.flowName}`}
                    name={`packAdSettings.${params.row.packIndex}.${params.row.flowName}.text_id`}
                    sx={localizationIssue
                      ? {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'error.main',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'error.main',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'error.main',
                          },
                          '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'error.main',
                          },
                        }
                      : {
                        '.MuiInputBase-select': { whiteSpace: 'wrap' },
                        '.MuiInputBase-root': { backgroundColor: 'white' },
                      }}
                    inputStartAdornment={localizationIssue?.message
                      ? (
                        <InputAdornment position="start" sx={{ mr: 0.5 }}>
                          <Tooltip title={localizationIssue.message} placement="top" arrow>
                            <ErrorOutlineIcon
                              color="error"
                              sx={{ fontSize: 18, flexShrink: 0 }}
                              data-testid={`adSettings-text-error-icon-${params.row.packIndex}-${params.row.flowName}`}
                            />
                          </Tooltip>
                        </InputAdornment>
                        )
                      : undefined}
                    options={params.row.texts.map((text) => ({
                      value: text.id,
                      label: text.name,
                    }))}
                    handleChange={handleAdSettingChange}
                  />
                </Box>
                {shouldShowAddLocTextButton ? (
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    sx={{ textTransform: 'none', px: 0, whiteSpace: 'nowrap', flexShrink: 0, alignSelf: 'center' }}
                    onClick={() => {
                      handleOpenEditLocTextModal(localizationIssue?.selectedTextId ?? null);
                    }}
                  >
                    Add loc text
                  </Button>
                ) : null}
              </Box>
            </Box>
          );
        },
      },
    ],
    [handleAdSettingChange, handleOpenEditLocTextModal, missingLocalizationByRow],
  );

  const getRowId = useCallback((row: AdSettingsPerPackRow) => row.id, []);

  const getRowClassName = useCallback(
    (params: { row: AdSettingsPerPackRow }) => {
      const classNames: string[] = [];

      if (params.row.flowIndex === 0) {
        classNames.push('pack-group-start');
      }

      if (params.row.isLastFlowInPack) {
        classNames.push('pack-group-end');
      }

      return classNames.join(' ');
    },
    [],
  );

  if (!isScalingEg && packCount < 1) {
    return (
      <Alert severity="info" variant="standard" sx={{ width: 'fit-content' }}>
        Add at least one creative pack to configure ad settings.
      </Alert>
    );
  }

  if (isLoadingAdSettings) {
    return (
      <Skeleton
        height={120}
        width="100%"
        sx={{ transform: 'translateY(0)', borderRadius: '8px' }}
      />
    );
  }

  if (isError) {
    return (
      <Alert
        severity="error"
        variant="standard"
        sx={{ width: 'fit-content' }}
        action={(
          <Button
            color="inherit"
            size="small"
            onClick={() => {
              void refetchAdSettings();
            }}
          >
            Retry
          </Button>
        )}
      >
        {(error as { cause?: { error?: string } })?.cause?.error ??
          'Error occurred. Please try again.'}
      </Alert>
    );
  }

  if (!adSettings || isEmpty(adSettings)) {
    return (
      <Alert severity="error" variant="standard" sx={{ width: 'fit-content' }}>
        Ad settings not found for selected ad sets.
      </Alert>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, width: '100%', mb: 0 }}>
      <Typography variant="body1" fontWeight={500}>
        {isScalingEg ? 'Ad Settings' : 'Ad Settings per Pack'}
      </Typography>
      <Box
        data-testid="adSettingsPerPackGrid"
        sx={{ position: 'relative', width: '100%', mb: 0 }}
      >
        <DataGrid
          rows={rows}
          columns={columns}
          slots={{
            row: CustomGridRow,
          }}
          getRowId={getRowId}
          getRowClassName={getRowClassName}
          autoHeight
          getRowHeight={() => 'auto'}
          getEstimatedRowHeight={() => 64}
          hideFooter
          hideFooterPagination
          disableColumnMenu
          disableColumnSorting
          disableRowSelectionOnClick
          checkboxSelection={false}
          sx={gridStyles}
        />
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'start', gap: 1 }}>
        <Typography variant="caption" color="inherit">
          Check page limits here:
        </Typography>
        <Button
          variant="text"
          size="small"
          component="a"
          href={FACEBOOK_AD_LIMITS_URL}
          target="_blank"
          rel="noopener noreferrer"
          startIcon={<LinkIcon />}
          sx={{ alignSelf: 'flex-start', textTransform: 'none', px: 1, fontWeight: 400 }}
        >
          {FACEBOOK_AD_LIMITS_URL}
        </Button>
      </Box>
      <EditLocTextModal
        autoAddLocalizedVersion
        textId={editingTextId}
        isOpen={isEditLocTextModalOpen}
        onClose={handleCloseEditLocTextModal}
        onSaved={handleLocTextModalSavedOrClosed}
      />
    </Box>
  );
};

export default SelectAdSettingsPerPack;
